import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Header from '@/components/ui/Header';
import { UserAreaHeader } from '@/pages/user/components/UserAreaHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import { setUserData } from '@/utils/cookies';
import { useGetUser, useLogoutUser } from '@/services';
import {
	SidebarProvider,
	type TutorialNavHighlightRect,
} from '@/context/SidebarContext';
import { useGetAvailableForms } from '@/services/queries/form';
import {
	ENABLE_USER_SITE_TUTORIAL,
	PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY,
} from '@/constants/session';
import {
	isSessionCallRoute,
	type SessionCallLayoutOutletContext,
	type VideoCallHeaderConfig,
} from '@/constants/sessionCall';
import { cn } from '@/lib/utils';
import {
	type SiteTutorialPhase,
	clearUserFirstAccess,
	clearUserSignupContext,
	filterAnswerableForms,
	forwardAuthRedirectNavigateOptions,
	AUTH_REDIRECT_CLUBES,
	resolvePostAuthClubFlowPath,
	hasSiteTutorialPending,
	hasUserFirstAccess,
	hasUserSignupContext,
	clearSiteTutorialPending,
} from '@/utils';
import { SiteTutorialNavSpotlight } from '@/pages/user/site-tutorial/SiteTutorialNavSpotlight';
import { SiteTutorialStepModal } from '@/pages/user/site-tutorial/SiteTutorialStepModal';
import { UserSiteTutorialWelcome } from '@/pages/user/site-tutorial/UserSiteTutorialWelcome';

type SpotlightPhase =
	| 'home_next'
	| 'home_schedule'
	| 'home_filters_tab'
	| 'home_filters_options'
	| 'profile'
	| 'ipbe'
	| 'clubs';

const SPOTLIGHT_SELECTOR_BY_PHASE: Partial<Record<SpotlightPhase, string>> = {
	home_next:
		'[data-sliding-panel-active="true"] [data-site-tutorial="home-next-panel"]',
	home_schedule: '[data-site-tutorial="home-schedule-button"]',
	home_filters_tab: '[data-sliding-tab-id="all"]',
	home_filters_options:
		'[data-sliding-panel-active="true"] [data-site-tutorial="home-filters-options"]',
	profile: '[data-site-tutorial="profile-root"]',
	ipbe: '[data-site-tutorial="ipbe-root"]',
	clubs: '[data-site-tutorial="clubs-root"]',
};

const SPOTLIGHT_PADDING_BY_PHASE: Partial<Record<SpotlightPhase, number>> = {
	home_schedule: 14,
};

const STEP_MODAL_CONFIG: Partial<
	Record<
		SiteTutorialPhase,
		{ title: string; description: string; stepLabel: string }
	>
> = {
	home_next: {
		title: 'Tela inicial de consultas',
		description:
			'Na aba Próximas, você acompanha suas consultas mais próximas em ordem.',
		stepLabel: 'Passo 3 de 9',
	},
	home_schedule: {
		title: 'Botão Agendar',
		description:
			'Use Agendar para marcar uma nova consulta sempre que precisar.',
		stepLabel: 'Passo 4 de 9',
	},
	home_filters_tab: {
		title: 'Aba de Filtros',
		description:
			'Aqui você muda para a visualização de filtros para buscar sessões específicas.',
		stepLabel: 'Passo 5 de 9',
	},
	home_filters_options: {
		title: 'Opções de filtros',
		description:
			'Nesta área você combina período e status para encontrar exatamente o que quer.',
		stepLabel: 'Passo 6 de 9',
	},
	profile: {
		title: 'Tela de Perfil',
		description:
			'No Perfil você gerencia seus dados pessoais e mantém suas informações atualizadas.',
		stepLabel: 'Passo 7 de 9',
	},
	ipbe: {
		title: 'Suas dimensões de energia (IPBE)',
		description:
			'Nesta tela você acompanha seu espaço de dimensões de energia na Plumaa. Conforme você avança na jornada, novos conteúdos e insights aparecem aqui.',
		stepLabel: 'Passo 8 de 9',
	},
	clubs: {
		title: 'Tela de Clubes',
		description:
			'Aqui você visualiza e gerencia seus clubes e benefícios dentro da Plumaa.',
		stepLabel: 'Passo 9 de 9',
	},
};

type UserLayoutShellProps = {
	showSiteTutorialFlow: boolean;
	siteTutorialPhase: SiteTutorialPhase | null;
	showTutorialModal: boolean;
	handleWelcomeContinue: () => void;
	handleTutorialNext: () => void;
	handleTutorialDismiss: () => void;
};

const UserLayoutShell = ({
	showSiteTutorialFlow,
	siteTutorialPhase,
	showTutorialModal,
	handleWelcomeContinue,
	handleTutorialNext,
	handleTutorialDismiss,
}: UserLayoutShellProps) => {
	const location = useLocation();
	const isVideoCallMode = isSessionCallRoute(location.pathname);
	const [videoCallHeaderConfig, setVideoCallHeaderConfig] =
		useState<VideoCallHeaderConfig | null>(null);

	const [dynamicSpotlightRect, setDynamicSpotlightRect] =
		useState<TutorialNavHighlightRect | null>(null);

	useEffect(() => {
		if (!isVideoCallMode) {
			setVideoCallHeaderConfig(null);
		}
	}, [isVideoCallMode]);

	const spotlightPhase =
		showSiteTutorialFlow && siteTutorialPhase
			? (siteTutorialPhase as SpotlightPhase)
			: null;

	const selector = spotlightPhase
		? SPOTLIGHT_SELECTOR_BY_PHASE[spotlightPhase]
		: null;

	useEffect(() => {
		if (!selector) {
			if (!showSiteTutorialFlow) {
				setDynamicSpotlightRect(null);
			}
			return;
		}

		const setRectFromElement = (el: Element | null) => {
			if (!el) return false;
			const r = el.getBoundingClientRect();
			const pad = spotlightPhase
				? SPOTLIGHT_PADDING_BY_PHASE[spotlightPhase] ?? 0
				: 0;
			setDynamicSpotlightRect({
				top: r.top - pad,
				left: r.left - pad,
				right: r.right + pad,
				bottom: r.bottom + pad,
			});
			return true;
		};

		const update = () => {
			const el = document.querySelector(selector);
			setRectFromElement(el);
		};

		update();
		const timer = window.setInterval(update, 120);
		window.addEventListener('resize', update);
		window.addEventListener('scroll', update, true);

		return () => {
			window.clearInterval(timer);
			window.removeEventListener('resize', update);
			window.removeEventListener('scroll', update, true);
		};
	}, [selector, spotlightPhase, showSiteTutorialFlow]);

	const activeSpotlightRect = dynamicSpotlightRect;
	const hasActiveSpotlight = activeSpotlightRect !== null;

	const genericModalConfig =
		siteTutorialPhase !== null
			? STEP_MODAL_CONFIG[siteTutorialPhase]
			: null;

	return (
		<section className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-zinc-50">
			{showSiteTutorialFlow &&
				hasActiveSpotlight &&
				activeSpotlightRect && (
					<SiteTutorialNavSpotlight hole={activeSpotlightRect} />
				)}
			{isVideoCallMode ? (
				<Header
					videoCallMode
					videoCallHeaderConfig={videoCallHeaderConfig}
					hideSidebarMenu
				/>
			) : (
				<UserAreaHeader />
			)}
			<main className="relative flex h-[calc(100vh-64px)] min-h-0 w-full">
				<section
					className={cn(
						'relative flex h-full min-h-0 w-full flex-1 overflow-y-scroll',
						isVideoCallMode
							? 'justify-stretch'
							: 'justify-center md:px-4',
					)}
				>
					{showSiteTutorialFlow && siteTutorialPhase === 'welcome' ? (
						<UserSiteTutorialWelcome
							onComplete={handleWelcomeContinue}
						/>
					) : (
						<Outlet context={{ setVideoCallHeaderConfig }} />
					)}
				</section>
				{showSiteTutorialFlow &&
					showTutorialModal &&
					genericModalConfig &&
					siteTutorialPhase !== 'welcome' && (
						<SiteTutorialStepModal
							title={genericModalConfig.title}
							description={genericModalConfig.description}
							stepLabel={genericModalConfig.stepLabel}
							onNext={handleTutorialNext}
							onDismiss={handleTutorialDismiss}
							desktopAnchorRect={activeSpotlightRect}
						/>
					)}
			</main>
		</section>
	);
};

const UserLayout = () => {
	const STEP_MODAL_DELAY_MS = 500;
	const SCREEN_CHANGE_MODAL_DELAY_MS = 1000;
	const WELCOME_TO_STEP1_DELAY_MS = 220;
	const navigate = useNavigate();
	const location = useLocation();
	const { data, isLoading } = useGetUser(
		{ isMe: true },
		{ refetchOnWindowFocus: false },
	);

	const { data: availableForms, isLoading: isLoadingAvailableForms } =
		useGetAvailableForms(
			{ page: 1, limit: 100 },
			{ refetchOnWindowFocus: false, enabled: !!data },
		);

	const answerableForms = useMemo(
		() => filterAnswerableForms(availableForms?.data),
		[availableForms?.data],
	);
	const hasPendingForm = answerableForms.length > 0;

	useEffect(() => {
		if (!isLoading && !data) {
			navigate('/login');
			return;
		}
		if (!data || isLoadingAvailableForms) {
			return;
		}

		setUserData(data);
		const userMetadata = (data?.metadata as any) || {};
		const isOnFormPage = location.pathname === '/formulario';
		const isOnFirstAccessPage = location.pathname === '/primeiro-acesso';
		const isOnClubesPage = location.pathname === AUTH_REDIRECT_CLUBES;
		const clubFlowOpts = forwardAuthRedirectNavigateOptions(location.state);
		const clubFlowPath = resolvePostAuthClubFlowPath(
			location.state,
			hasPendingForm,
		);

		if (!data.employee && hasUserFirstAccess()) {
			clearUserFirstAccess();
		}

		if (clubFlowPath && !isOnClubesPage) {
			if (clubFlowPath === '/formulario' && !isOnFormPage) {
				navigate('/formulario', { replace: true, ...clubFlowOpts });
				return;
			}
			if (clubFlowPath === AUTH_REDIRECT_CLUBES) {
				navigate(AUTH_REDIRECT_CLUBES, { replace: true });
				return;
			}
		}

		const allowHomeAfterFormFlow =
			typeof window !== 'undefined' &&
			sessionStorage.getItem(PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY) ===
				'1';

		if (hasPendingForm && !isOnFormPage) {
			if (
				allowHomeAfterFormFlow &&
				(location.pathname === '/home' ||
					location.pathname === '/perfil')
			) {
				return;
			}
			/** Boas-vindas em `/primeiro-acesso` vêm antes do formulário (somente employees B2B). */
			if (
				hasUserFirstAccess() &&
				hasUserSignupContext() &&
				!isOnFirstAccessPage &&
				data.employee
			) {
				navigate('/primeiro-acesso', {
					replace: true,
					...forwardAuthRedirectNavigateOptions(location.state),
				});
				return;
			}
			if (isOnFirstAccessPage) {
				return;
			}
			navigate('/formulario', {
				...forwardAuthRedirectNavigateOptions(location.state),
			});
			return;
		}

		const isHomeOrProfile =
			location.pathname === '/home' || location.pathname === '/perfil';

		if (!hasPendingForm && isHomeOrProfile && allowHomeAfterFormFlow) {
			try {
				sessionStorage.removeItem(
					PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY,
				);
			} catch {
				/* ignore */
			}
			return;
		}

		if (
			!hasPendingForm &&
			!isOnFirstAccessPage &&
			isHomeOrProfile &&
			hasUserFirstAccess() &&
			hasUserSignupContext() &&
			data.employee
		) {
			navigate('/primeiro-acesso', { replace: true });
			return;
		}

		if (typeof window !== 'undefined' && !hasPendingForm) {
			try {
				sessionStorage.removeItem(
					PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY,
				);
			} catch {
				/* ignore */
			}
		}

		if (
			userMetadata.company_code &&
			!data?.is_member &&
			window.location.pathname !== '/beneficios/planos'
		) {
			navigate('/beneficios/planos');
		}
	}, [
		isLoading,
		data,
		navigate,
		hasPendingForm,
		isLoadingAvailableForms,
		location.pathname,
		location.state,
	]);

	const { mutate: logout } = useLogoutUser(() => navigate('/login'));

	const [siteTutorialPhase, setSiteTutorialPhase] =
		useState<SiteTutorialPhase | null>(null);
	const [showTutorialModal, setShowTutorialModal] = useState(true);
	const modalTimerRef = useRef<number | null>(null);

	const clearModalTimer = () => {
		if (modalTimerRef.current !== null) {
			window.clearTimeout(modalTimerRef.current);
			modalTimerRef.current = null;
		}
	};

	const scheduleNextModal = (delayMs: number) => {
		clearModalTimer();
		modalTimerRef.current = window.setTimeout(() => {
			setShowTutorialModal(true);
			modalTimerRef.current = null;
		}, delayMs);
	};

	useEffect(() => {
		return () => clearModalTimer();
	}, []);

	useEffect(() => {
		if (!ENABLE_USER_SITE_TUTORIAL) {
			if (hasSiteTutorialPending()) {
				clearSiteTutorialPending();
			}
			if (hasUserSignupContext()) {
				clearUserSignupContext();
			}
			return;
		}

		if (!data || isLoadingAvailableForms || hasPendingForm) return;
		if (location.pathname !== '/home') return;
		if (!hasSiteTutorialPending()) return;
		if (!hasUserSignupContext()) return;
		clearSiteTutorialPending();
		clearUserSignupContext();
		setSiteTutorialPhase('welcome');
	}, [data, isLoadingAvailableForms, hasPendingForm, location.pathname]);

	const showSiteTutorialFlow =
		ENABLE_USER_SITE_TUTORIAL &&
		!!data &&
		!isLoadingAvailableForms &&
		!hasPendingForm &&
		siteTutorialPhase !== null;

	const handleWelcomeContinue = () => {
		setShowTutorialModal(false);
		setSiteTutorialPhase('home_next');
		navigate('/home', { replace: true });
		scheduleNextModal(WELCOME_TO_STEP1_DELAY_MS);
	};

	useEffect(() => {
		if (siteTutorialPhase === 'home_filters_options') {
			if (location.pathname !== '/home') return;
			let tries = 0;
			const id = window.setInterval(() => {
				const btn = document.querySelector<HTMLButtonElement>(
					'[data-sliding-tab-id="all"]',
				);
				if (btn) {
					btn.click();
					window.clearInterval(id);
					return;
				}
				tries += 1;
				if (tries > 20) {
					window.clearInterval(id);
				}
			}, 80);
			return () => window.clearInterval(id);
		}
	}, [siteTutorialPhase, location.pathname]);

	useEffect(() => {
		if (!siteTutorialPhase) return;
		const expectedPath: Partial<Record<SiteTutorialPhase, string>> = {
			home_next: '/home',
			home_schedule: '/home',
			home_filters_tab: '/home',
			home_filters_options: '/home',
			profile: '/perfil',
			ipbe: '/ipbe',
			clubs: '/clubes',
		};
		const target = expectedPath[siteTutorialPhase];
		if (target && location.pathname !== target) {
			navigate(target, { replace: true });
		}
	}, [siteTutorialPhase, location.pathname, navigate]);

	const handleTutorialNext = () => {
		let nextPhase: SiteTutorialPhase | null = null;
		let targetPath: string | undefined;
		switch (siteTutorialPhase) {
			case 'home_next':
				nextPhase = 'home_schedule';
				break;
			case 'home_schedule':
				nextPhase = 'home_filters_tab';
				break;
			case 'home_filters_tab':
				nextPhase = 'home_filters_options';
				break;
			case 'home_filters_options':
				nextPhase = 'profile';
				targetPath = '/perfil';
				break;
			case 'profile':
				nextPhase = 'clubs';
				targetPath = '/clubes';
				break;
			// case 'ipbe': desabilitado
			//   nextPhase = 'clubs';
			//   targetPath = '/clubes';
			//   break;
			case 'clubs':
				nextPhase = null;
				targetPath = '/home';
				break;
			default:
				return;
		}

		setShowTutorialModal(false);
		clearModalTimer();
		setSiteTutorialPhase(nextPhase);

		if (targetPath) {
			navigate(targetPath, { replace: true });
		}

		if (nextPhase !== null) {
			const isScreenChange =
				!!targetPath && targetPath !== location.pathname;
			scheduleNextModal(
				isScreenChange
					? SCREEN_CHANGE_MODAL_DELAY_MS
					: STEP_MODAL_DELAY_MS,
			);
		}
	};

	const handleTutorialDismiss = () => {
		setShowTutorialModal(false);
		clearModalTimer();
		setSiteTutorialPhase(null);
		navigate('/home', { replace: true });
	};

	if (!data) return null;

	return (
		<SidebarProvider
			items={[]}
			onLogout={logout}
			showProfileFooter={false}
			showSidebarLogout={false}
		>
			<UserLayoutShell
				showSiteTutorialFlow={showSiteTutorialFlow}
				siteTutorialPhase={siteTutorialPhase}
				showTutorialModal={showTutorialModal}
				handleWelcomeContinue={handleWelcomeContinue}
				handleTutorialNext={handleTutorialNext}
				handleTutorialDismiss={handleTutorialDismiss}
			/>
		</SidebarProvider>
	);
};

export { UserLayout };
export type { SessionCallLayoutOutletContext };
